import {
  type CultureSatisfactionRating,
  ENGAGEMENT_DIMENSIONS,
  satisfactionRatingFor,
} from "@compono/assessments-kit";

/**
 * The nine climate questions, in the words the survey asked them.
 *
 * The report is served the nine as keys — `financial`, `turnoverIntention` — because engage
 * stores figures and not copy, so the wording has to live somewhere the page can reach. Here
 * rather than in `InsightsReport/copy.ts` because the engagement tab shows the same nine.
 *
 *
 * The four company questions come first and the five personal ones after, which is the order
 * `ENGAGEMENT_DIMENSIONS` fixes and the order a division's scores are read in.
 */
export const ENGAGEMENT_COPY: Record<
  string,
  { label: string; question: string }
> = {
  financial: {
    label: "Financial",
    question: "Our company is performing well financially.",
  },
  operational: {
    label: "Operational",
    question: "Our company runs effectively and efficiently.",
  },
  learningGrowth: {
    label: "Learning / Growth",
    question: "Our company learns and improves from past experiences.",
  },
  customerQuality: {
    label: "Customer Quality",
    question:
      "Our company produces quality products or services that meet customer expectations.",
  },
  jobEngagement: {
    label: "Job Engagement",
    question: "I am engaged in my job.",
  },
  jobSatisfaction: {
    label: "Job Satisfaction",
    question: "I am satisfied with the tasks and responsibilities of my job.",
  },
  turnoverIntention: {
    label: "Turnover Intention",
    question: "I am unlikely to leave the company in the next year.",
  },
  workMotivation: {
    label: "Work Motivation",
    question: "I am motivated to achieve work goals and objectives.",
  },
  overallClimate: {
    label: "Overall Climate",
    question: "My work environment is positive and supportive.",
  },
};

/**
 * The two halves the design reads the nine in: what people say about the company, and what they
 * say about their own working life.
 */
export const COMPANY_DIMENSIONS = ENGAGEMENT_DIMENSIONS.slice(0, 4);

export const ATMOSPHERE_DIMENSIONS = ENGAGEMENT_DIMENSIONS.slice(4);

export const COMPANY_HEADING = "Company performance";

export const ATMOSPHERE_HEADING = "Work atmosphere";

/** The half of a set of scores that belongs to one of those two columns, in survey order. */
export const withinGroup = <Score extends { dimension: string }>(
  scores: Score[],
  dimensions: readonly string[],
): Score[] => scores.filter(({ dimension }) => dimensions.includes(dimension));

/**
 * What a set of scores averages to, at the one decimal place every score in the report is shown to.
 *
 * Here rather than in the chart that used to hold it, because the report now averages the same two
 * columns in two places — the climate chart in the appendix and the summary card under the findings
 * — and a mean rounded one way in one and another way in the other is the same figure disagreeing
 * with itself on one page.
 *
 * An average of the scores actually given, so a campaign that asked only some of a column's
 * questions averages the ones it asked rather than reporting a figure the rows under it do not add
 * to.
 */
export const averageScore = (scores: { score: number }[]) =>
  Math.round(
    (scores.reduce((total, { score }) => total + score, 0) / scores.length) *
      10,
  ) / 10;

/** A dimension the survey stopped asking still has a key, so it reads as itself rather than blank. */
export const engagementLabel = (dimension: string) =>
  ENGAGEMENT_COPY[dimension]?.label ?? dimension;

export const engagementQuestion = (dimension: string) =>
  ENGAGEMENT_COPY[dimension]?.question;

/**
 * The five answers a climate question offers, lowest first.
 *
 * A distribution is a count per band, so the order here is the order of the counts — it is the
 * scale itself, not a presentation choice, and both the engagement tab and the report read it.
 */
export const RESPONSE_BAND_LABELS = [
  "Strongly disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly agree",
] as const;

export const AVERAGE_SCORE = "Average score";

export const OVERALL = "Overall";

/**
 * How each band of the climate scale is written and coloured, keyed by the rating itself.
 *
 * Keyed rather than matched: engage words the rating on a document from
 * `satisfactionRatingFor`, and this page words a mean it computes from the same function, so the
 * two now meet on a key instead of on a lower-cased string comparison that fell through to raw
 * text the moment either side reworded a band.
 *
 * The colours are the design's own rather than the bar's five. They are darker relatives of them:
 * `#FFE29B` reads as a fifth of a bar and not as a word, and the middle of the scale is where that
 * matters most.
 */
export const SATISFACTION_BANDS: Record<
  CultureSatisfactionRating,
  { label: string; colour: string }
> = {
  "very dissatisfied": { label: "Very Dissatisfied", colour: "#D11D05" },
  dissatisfied: { label: "Dissatisfied", colour: "#E24C2D" },
  neutral: { label: "Neutral", colour: "#F89306" },
  satisfied: { label: "Satisfied", colour: "#14B8A6" },
  "very satisfied": { label: "Very Satisfied", colour: "#139C8F" },
};

/** The band a mean falls in, which is the wording the engagement tab has always used. */
export const satisfactionRating = (average: number) =>
  SATISFACTION_BANDS[satisfactionRatingFor(average)].label;
